/**
 * Settings Slice
 * 
 * Manages application settings state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import settingsService from '../../services/settingsService';

// Initial state
// Support both string arrays (legacy) and object arrays with {id, name}
const initialState = {
  complaintTypes: [],
  complaintStatuses: [],
  complaintPriorities: [],
  officerDesignations: [],
  departments: [],
  authorityPrivileges: [],
  cvoPrivileges: [],
  isLoading: false,
  error: null,
  dropdownsLoaded: false, // Track if dropdowns have been loaded from API
};

// Async thunks
export const fetchSettings = createAsyncThunk(
  'settings/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getSettings();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplaintTypes = createAsyncThunk(
  'settings/fetchComplaintTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getComplaintTypes();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchComplaintStatuses = createAsyncThunk(
  'settings/fetchComplaintStatuses',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getComplaintStatuses();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPriorities = createAsyncThunk(
  'settings/fetchPriorities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getPriorities();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'settings/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getDepartments();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDesignations = createAsyncThunk(
  'settings/fetchDesignations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getDesignations();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAuthorityPrivileges = createAsyncThunk(
  'settings/fetchAuthorityPrivileges',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getAuthorityPrivileges();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchCVOPrivileges = createAsyncThunk(
  'settings/fetchCVOPrivileges',
  async (_, { rejectWithValue }) => {
    try {
      const response = await settingsService.getCVOPrivileges();
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch all authority dropdowns in one call
 * Maps API response keys to Redux state keys:
 * - complaint_types -> complaintTypes
 * - complaint_statuses -> complaintStatuses
 * - departments -> departments
 * - designations -> officerDesignations
 * - priorities -> complaintPriorities
 * - authority_privileges -> authorityPrivileges
 */
export const fetchAuthorityDropdowns = createAsyncThunk(
  'settings/fetchAuthorityDropdowns',
  async (_, { rejectWithValue }) => {
    try {
      console.log('fetchAuthorityDropdowns thunk: Starting...');
      const response = await settingsService.getAuthorityDropdowns();
      console.log('fetchAuthorityDropdowns thunk: Service response:', response);
      if (response.success) {
        // Map API response keys to Redux state keys
        const data = response.data;
        const mappedData = {
          complaintTypes: data.complaint_types || [],
          complaintStatuses: data.complaint_statuses || [],
          departments: data.departments || [],
          officerDesignations: data.designations || [],
          complaintPriorities: data.priorities || [],
          authorityPrivileges: data.authority_privileges || [],
        };
        console.log('fetchAuthorityDropdowns thunk: Mapped data:', mappedData);
        return mappedData;
      }
      console.error('fetchAuthorityDropdowns thunk: Response not successful:', response.message);
      return rejectWithValue(response.message);
    } catch (error) {
      console.error('fetchAuthorityDropdowns thunk: Error:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSettings = createAsyncThunk(
  'settings/update',
  async (settingsData, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateSettings(settingsData);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addSettingOption = createAsyncThunk(
  'settings/addOption',
  async ({ type, data }, { rejectWithValue }) => {
    try {
      const response = await settingsService.addSettingOption(type, data);
      if (response.success) {
        return { type, data: response.data };
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateSettingOption = createAsyncThunk(
  'settings/updateOption',
  async ({ type, id, data }, { rejectWithValue }) => {
    try {
      const response = await settingsService.updateSettingOption(type, id, data);
      if (response.success) {
        return { type, id, data: response.data };
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSettingOption = createAsyncThunk(
  'settings/deleteOption',
  async ({ type, id }, { rejectWithValue }) => {
    try {
      const response = await settingsService.deleteSettingOption(type, id);
      if (response.success) {
        return { type, id };
      }
      return rejectWithValue(response.message);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Settings slice
const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    // Synchronous actions for local updates
    addComplaintType: (state, action) => {
      if (!state.complaintTypes.includes(action.payload)) {
        state.complaintTypes.push(action.payload);
      }
    },
    removeComplaintType: (state, action) => {
      state.complaintTypes = state.complaintTypes.filter(t => t !== action.payload);
    },
    addComplaintStatus: (state, action) => {
      if (!state.complaintStatuses.includes(action.payload)) {
        state.complaintStatuses.push(action.payload);
      }
    },
    removeComplaintStatus: (state, action) => {
      state.complaintStatuses = state.complaintStatuses.filter(s => s !== action.payload);
    },
    addDepartment: (state, action) => {
      if (!state.departments.includes(action.payload)) {
        state.departments.push(action.payload);
      }
    },
    removeDepartment: (state, action) => {
      state.departments = state.departments.filter(d => d !== action.payload);
    },
    addDesignation: (state, action) => {
      if (!state.officerDesignations.includes(action.payload)) {
        state.officerDesignations.push(action.payload);
      }
    },
    removeDesignation: (state, action) => {
      state.officerDesignations = state.officerDesignations.filter(d => d !== action.payload);
    },
    addAuthorityPrivilege: (state, action) => {
      if (!state.authorityPrivileges.includes(action.payload)) {
        state.authorityPrivileges.push(action.payload);
      }
    },
    removeAuthorityPrivilege: (state, action) => {
      state.authorityPrivileges = state.authorityPrivileges.filter(p => p !== action.payload);
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all settings
    builder
      .addCase(fetchSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        Object.assign(state, action.payload);
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch complaint types
    builder
      .addCase(fetchComplaintTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintTypes = action.payload;
      })
      .addCase(fetchComplaintTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch complaint statuses
    builder
      .addCase(fetchComplaintStatuses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchComplaintStatuses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintStatuses = action.payload;
      })
      .addCase(fetchComplaintStatuses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch priorities
    builder
      .addCase(fetchPriorities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPriorities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.complaintPriorities = action.payload;
      })
      .addCase(fetchPriorities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch departments
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = action.payload;
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch designations
    builder
      .addCase(fetchDesignations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.officerDesignations = action.payload;
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch authority privileges
    builder
      .addCase(fetchAuthorityPrivileges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuthorityPrivileges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.authorityPrivileges = action.payload;
      })
      .addCase(fetchAuthorityPrivileges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch CVO privileges
    builder
      .addCase(fetchCVOPrivileges.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCVOPrivileges.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cvoPrivileges = action.payload;
      })
      .addCase(fetchCVOPrivileges.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update settings
    builder
      .addCase(updateSettings.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.isLoading = false;
        Object.assign(state, action.payload);
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Add setting option
    builder
      .addCase(addSettingOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSettingOption.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, data } = action.payload;
        if (state[type] && Array.isArray(state[type])) {
          state[type].push(data);
        }
      })
      .addCase(addSettingOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Update setting option
    builder
      .addCase(updateSettingOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSettingOption.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, id, data } = action.payload;
        if (state[type] && Array.isArray(state[type])) {
          const index = state[type].findIndex(item => 
            (typeof item === 'object' ? item.id === id : item === id)
          );
          if (index !== -1) {
            state[type][index] = data;
          }
        }
      })
      .addCase(updateSettingOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Delete setting option
    builder
      .addCase(deleteSettingOption.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSettingOption.fulfilled, (state, action) => {
        state.isLoading = false;
        const { type, id } = action.payload;
        if (state[type] && Array.isArray(state[type])) {
          state[type] = state[type].filter(item => 
            (typeof item === 'object' ? item.id !== id : item !== id)
          );
        }
      })
      .addCase(deleteSettingOption.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Fetch authority dropdowns
    builder
      .addCase(fetchAuthorityDropdowns.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAuthorityDropdowns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dropdownsLoaded = true;
        // Update all dropdown data from API response
        state.complaintTypes = action.payload.complaintTypes;
        state.complaintStatuses = action.payload.complaintStatuses;
        state.departments = action.payload.departments;
        state.officerDesignations = action.payload.officerDesignations;
        state.complaintPriorities = action.payload.complaintPriorities;
        state.authorityPrivileges = action.payload.authorityPrivileges;
      })
      .addCase(fetchAuthorityDropdowns.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  addComplaintType,
  removeComplaintType,
  addComplaintStatus,
  removeComplaintStatus,
  addDepartment,
  removeDepartment,
  addDesignation,
  removeDesignation,
  addAuthorityPrivilege,
  removeAuthorityPrivilege,
  clearError,
} = settingsSlice.actions;

// Export selectors
export const selectComplaintTypes = (state) => state.settings.complaintTypes;
export const selectComplaintStatuses = (state) => state.settings.complaintStatuses;
export const selectComplaintPriorities = (state) => state.settings.complaintPriorities;
export const selectOfficerDesignations = (state) => state.settings.officerDesignations;
export const selectDepartments = (state) => state.settings.departments;
export const selectAuthorityPrivileges = (state) => state.settings.authorityPrivileges;
export const selectCVOPrivileges = (state) => state.settings.cvoPrivileges;
export const selectSettingsLoading = (state) => state.settings.isLoading;
export const selectSettingsError = (state) => state.settings.error;
export const selectDropdownsLoaded = (state) => state.settings.dropdownsLoaded;

// Helper selectors that extract names from objects (for backward compatibility)
export const selectComplaintTypeNames = (state) => {
  const types = state.settings.complaintTypes;
  return types.map(item => typeof item === 'object' ? item.name : item);
};

export const selectComplaintStatusNames = (state) => {
  const statuses = state.settings.complaintStatuses;
  return statuses.map(item => typeof item === 'object' ? item.name : item);
};

export const selectDepartmentNames = (state) => {
  const depts = state.settings.departments;
  return depts.map(item => typeof item === 'object' ? item.name : item);
};

export const selectPriorityNames = (state) => {
  const priorities = state.settings.complaintPriorities;
  return priorities.map(item => typeof item === 'object' ? item.name : item);
};

// Export reducer
export default settingsSlice.reducer;

